// src/pages/AppGuide/index.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './index.scss';
import PexarLogo from './assets/pexar_logo.png';
import FramesImg from './assets/download_bg_center.png';
import QRCodeProdImg from './assets/qrcode_prod.png';
import { getDownloadAnalyticsParams, isInviteLink, trackEvent } from '../../services/analytics';

const STORE_URLS = {
  ios: 'https://apps.apple.com/app/6756567527',
  android: 'https://play.google.com/store/apps/details?id=com.pexar.mobile'
};

const ANDROID_APP_PACKAGE = 'com.pexar.mobile';
const ANDROID_INTENT_ATTEMPTED_KEY = 'appIntentAttempted';

const APK_DOWNLOAD_URL = 'https://s3.pexar.com/apks/pexar-android-v2.3.42-34-prod-release.apk';

const detectDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
  const isIOS = /iPad|iPhone|iPod/.test(userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /android/i.test(userAgent);

  return { isIOS, isAndroid };
};

const getStoreUrl = () => {
  const { isIOS } = detectDevice();

  if (isIOS) {
    return STORE_URLS.ios;
  }

  return null;
};

const getCurrentPlatformUrl = (
  targetHost = window.location.host || window.location.hostname,
  shouldMarkIntentAttempted = false
) => {
  const protocol = window.location.protocol || 'https:';
  const { pathname, search } = window.location;
  const searchParams = new URLSearchParams(search);

  if (shouldMarkIntentAttempted) {
    searchParams.set(ANDROID_INTENT_ATTEMPTED_KEY, '1');
  }

  const queryString = searchParams.toString();

  return `${protocol}//${targetHost}${pathname}${queryString ? `?${queryString}` : ''}`;
};

const hasAttemptedAndroidIntent = () => (
  new URLSearchParams(window.location.search).get(ANDROID_INTENT_ATTEMPTED_KEY) === '1'
);

const getAndroidIntentUrl = () => {
  const host = window.location.host || window.location.hostname;
  const { pathname, search } = window.location;
  const fallbackUrl = encodeURIComponent(getCurrentPlatformUrl(host, true));

  return `intent://${host}${pathname}${search}`
    + `#Intent;scheme=https;package=${ANDROID_APP_PACKAGE};`
    + `S.browser_fallback_url=${fallbackUrl};end`;
};

const getStoreButtonTextKey = () => {
  const { isIOS, isAndroid } = detectDevice();

  if (isIOS) {
    return 'appGuide.downloadAppStore';
  }

  if (isAndroid) {
    return 'appGuide.downloadGooglePlay';
  }

  return 'appGuide.downloadOfficialStore';
};

const getDownloadEventParams = (params = {}) => {
  return {
    page_path: window.location.pathname,
    ...getDownloadAnalyticsParams(),
    ...params
  };
};

const trackDownloadEvent = (eventName, params = {}) => {
  trackEvent(eventName, getDownloadEventParams(params));
};

function AppGuide() {
  const { t } = useTranslation();
  const [showQR, setShowQR] = useState(false);
  const { isIOS } = detectDevice();
  const storeButtonText = t(getStoreButtonTextKey());
  const isInvite = isInviteLink();

  // 尝试打开或下载APP
  const openOrDownloadApp = useCallback((isAutoOpen = false) => {
    const { isAndroid } = detectDevice();
    const trigger = isAutoOpen ? 'auto' : 'manual';

    if (isAndroid) {
      if (hasAttemptedAndroidIntent()) {
        if (!isAutoOpen) {
          trackDownloadEvent('download_store_click', {
            target: 'google_play',
            trigger
          });
          window.location.href = STORE_URLS.android;
        }

        return;
      }

      trackDownloadEvent('deeplink_attempt', {
        target: 'intent',
        trigger
      });
      window.location.href = getAndroidIntentUrl();
      return;
    }

    const storeUrl = getStoreUrl();

    if (!storeUrl) {
      // 在 PC（非iOS非安卓）浏览器上，直接弹出二维码提示
      setShowQR(true);
      return;
    }

    trackDownloadEvent('download_store_click', {
      target: 'app_store',
      trigger
    });
    window.location.href = storeUrl;
  }, []);

  useEffect(() => {
    if (hasAttemptedAndroidIntent()) {
      trackDownloadEvent('deeplink_fallback', {
        target: 'intent'
      });
    }
  }, []);

  useEffect(() => {
    const { isIOS } = detectDevice();

    if (isIOS) {
      return;
    }

    openOrDownloadApp(true);
  }, [openOrDownloadApp]);

  const renderStoreButton = () => (
    <button className="open-btn" onClick={() => openOrDownloadApp(false)} type="button">
      {storeButtonText}
    </button>
  );

  const renderApkButton = () => (
    <a
      className="apk-btn"
      href={APK_DOWNLOAD_URL}
      download
      onClick={() => trackDownloadEvent('download_apk_click', { target: 'apk' })}
    >
      {t('appGuide.downloadApk')}
    </a>
  );

  return (
    <div className="app-guide">
      <div className="app-actions">
        <img src={PexarLogo} alt="" className="app-logo" />
        <div className="app-name">Pexar</div>
        <div className="buttons">
          {renderStoreButton()}
          {!isIOS && renderApkButton()}
        </div>
        {isInvite ? (
          <div className="manual-open">
            <h3 className="manual-open-title">{t('appGuide.manualOpen.title')}</h3>
            <p className="manual-open-subtitle">{t('appGuide.manualOpen.copyTitle')}</p>
            <p className="manual-open-desc">{t('appGuide.manualOpen.copyDesc')}</p>
            <p className="manual-open-or">{t('appGuide.manualOpen.orTitle')}</p>
            <ol className="manual-open-steps">
              <li>{t('appGuide.manualOpen.step1')}</li>
              <li>{t('appGuide.manualOpen.step2')}</li>
              <li>{t('appGuide.manualOpen.step3')}</li>
            </ol>
          </div>
        ) : (
          <img src={FramesImg} alt="" className="app-frames" />
        )}
      </div>

      {showQR && (
        <div className="qr-modal-overlay" onClick={() => setShowQR(false)}>
          <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="qr-close-btn" onClick={() => setShowQR(false)} type="button">
              &times;
            </button>
            <h3>{t('appGuide.qrTitle')}</h3>
            <p>{t('appGuide.qrDesc')}</p>
            <img
              src={QRCodeProdImg}
              alt={t('appGuide.qrAlt')}
              className="qr-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AppGuide;
